import type { ValidationIssueFamily } from './validationIssueFamilies';

export type ValidationNextPathType =
  | 'direct-repair'
  | 'detail-inspection'
  | 'scope-review'
  | 'hybrid-review';

export type ValidationDecisionActionKind =
  | 'repair'
  | 'detail'
  | 'condition'
  | 'rerun'
  | 'highlight';

export type ValidationDecisionConfidence = 'high' | 'medium' | 'low';
export type ValidationRoutingDensity = 'high' | 'medium' | 'low';

export interface ValidationDecisionAction {
  kind: ValidationDecisionActionKind;
  label: string;
  reason: string;
}

export interface ValidationSwitchPathGuidance {
  pathType: Exclude<ValidationNextPathType, 'hybrid-review'>;
  label: string;
  signals: string[];
}

export interface ValidationRoutingTelemetry {
  primaryPath: ValidationNextPathType;
  alternatePath: ValidationNextPathType | null;
  confidence: ValidationDecisionConfidence;
  ambiguityReason?: string;
  conditionStudioAllowed: boolean;
  conditionStudioReasons: string[];
  heuristicTags: string[];
  scoreBreakdown: {
    directRepair: number;
    scopeReview: number;
    detailInspection: number;
  };
}

export interface ValidationIssueDecision {
  pathType: ValidationNextPathType;
  pathLabel: string;
  pathSubtitle?: string;
  routingDensity: ValidationRoutingDensity;
  pathReason: string;
  unresolvedReason: string;
  whyPath: string;
  whyThisPath: string[];
  prefersSurgery: boolean;
  confidence: ValidationDecisionConfidence;
  confidenceLabel: string;
  confidenceReason: string;
  primaryAction: ValidationDecisionAction;
  primaryActionSummary: string;
  alternatePath: ValidationNextPathType | null;
  alternatePathLabel?: string;
  alternateActions: ValidationDecisionAction[];
  reasonForAmbiguity?: string;
  repairSignals: string[];
  scopeSignals: string[];
  detailSignals: string[];
  switchGuidance: ValidationSwitchPathGuidance[];
  switchGuidanceDefaultOpen: boolean;
  compactSwitchGuidance?: string;
  conditionStudioAllowed: boolean;
  conditionStudioReason?: string;
  telemetry: ValidationRoutingTelemetry;
}

interface RoutingAssessment {
  directRepairScore: number;
  scopeReviewScore: number;
  detailInspectionScore: number;
  repairSignals: string[];
  scopeSignals: string[];
  detailSignals: string[];
  conditionStudioReasons: string[];
  heuristicTags: string[];
  repeatedPattern: boolean;
  dataLooksMissing: boolean;
  dataLooksComplete: boolean;
  applicabilityInDoubt: boolean;
  knownExceptionPattern: boolean;
  ambiguousFailPoint: boolean;
  scopeEligible: boolean;
}

type SignalBucket = 'repair' | 'scope' | 'detail';

const STRONG_SCOPE_PATTERNS: Array<{ patterns: string[]; reason: string; tag: string }> = [
  {
    patterns: ['FALSE POSITIVE', 'VALID EXCEPTION', 'KNOWN EXCEPTION'],
    reason: 'The issue already reads like a valid-exception or false-positive pattern.',
    tag: 'scope:exception-pattern',
  },
  {
    patterns: ['DOES NOT APPLY', 'NOT APPLICABLE', 'APPLICABILITY', 'SCOPE MISMATCH', 'RULE TOO BROAD'],
    reason: 'The validation rule itself may be applying too broadly to this set of items.',
    tag: 'scope:applicability',
  },
  {
    patterns: ['EXCLUSION', 'EXEMPT', 'EXEMPTION'],
    reason: 'The affected items may belong to an exclusion-worthy class.',
    tag: 'scope:exclusion',
  },
  {
    patterns: ['TEMPORARY SYSTEM', 'TEMPORARY WORKFLOW'],
    reason: 'Temporary systems often require narrower targeting instead of repeated direct edits.',
    tag: 'scope:temporary',
  },
];

const PLAUSIBLE_SCOPE_PATTERNS: Array<{ patterns: string[]; reason: string; tag: string }> = [
  {
    patterns: ['WORKFLOW-DEPENDENT', 'WORKFLOW DEPENDENT', 'PROFILE', 'CATEGORY-DEPENDENT', 'CATEGORY DEPENDENT'],
    reason: 'The issue appears to vary by workflow, profile, or category-specific logic.',
    tag: 'scope:workflow',
  },
  {
    patterns: ['OPTIONAL', 'EXCEPTION CLASS', 'NARROWER RULE'],
    reason: 'The affected pattern may belong to a narrower or optional rule family.',
    tag: 'scope:narrower-rule',
  },
  {
    patterns: ['ALREADY VALID', 'STRUCTURALLY VALID', 'CURRENT DATA LOOKS VALID', 'CURRENT DATA ALREADY'],
    reason: 'The current data may already be valid, which weakens a pure repair-first interpretation.',
    tag: 'scope:data-valid',
  },
];

const MISSING_DATA_PATTERNS = [
  ' MISSING ',
  'EMPTY',
  'NOT FOUND',
  'NO MODEL VALUES',
  'NO VALUES',
  'INCOMPLETE',
  'REQUIRED EXPORT DATA',
  'MAPPED PARAMETER',
];

const COMPLETE_DATA_PATTERNS = [
  'ALREADY VALID',
  'STRUCTURALLY VALID',
  'CURRENT DATA LOOKS VALID',
  'CURRENT DATA ALREADY',
  'MATCHES EXPECTED FORMAT',
];

function pushUnique(target: string[], value: string) {
  if (!target.includes(value)) {
    target.push(value);
  }
}

function hasAnySignal(text: string, patterns: string[]): boolean {
  return patterns.some((pattern) => text.includes(pattern));
}

function buildRoutingContextText(family: ValidationIssueFamily): string {
  return [
    family.title,
    family.summary,
    family.whyItMatters,
    family.recommendedFixPath,
    family.targetLabel,
    ...family.checkTitles,
    ...family.rawIssues.flatMap((issue) => [
      issue.Message,
      issue.Recommendation,
      issue.Category,
      ...Object.values(issue.AdditionalData || {}),
    ]),
  ]
    .join(' ')
    .replace(/\s+/g, ' ')
    .toUpperCase();
}

function buildAction(
  kind: ValidationDecisionActionKind,
  label: string,
  reason: string
): ValidationDecisionAction {
  return { kind, label, reason };
}

function isObjectTypeCategoryMatchFamily(family: ValidationIssueFamily): boolean {
  return family.validationIds.includes(4) &&
    family.affectedItems.some((item) => item.trim().toUpperCase() === 'OBJECT-TYPE');
}

function addSignal(
  assessment: RoutingAssessment,
  bucket: SignalBucket,
  points: number,
  reason: string,
  tag: string
) {
  if (bucket === 'repair') {
    assessment.directRepairScore += points;
    pushUnique(assessment.repairSignals, reason);
  } else if (bucket === 'scope') {
    assessment.scopeReviewScore += points;
    pushUnique(assessment.scopeSignals, reason);
  } else {
    assessment.detailInspectionScore += points;
    pushUnique(assessment.detailSignals, reason);
  }

  pushUnique(assessment.heuristicTags, tag);
}

function assessRoutingSignals(family: ValidationIssueFamily): RoutingAssessment {
  const routingContext = buildRoutingContextText(family);
  const assessment: RoutingAssessment = {
    directRepairScore: 0,
    scopeReviewScore: 0,
    detailInspectionScore: 0,
    repairSignals: [],
    scopeSignals: [],
    detailSignals: [],
    conditionStudioReasons: [],
    heuristicTags: [],
    repeatedPattern:
      family.issueCount >= 6 || family.affectedCount >= 5 || family.failingTypes.length >= 2,
    dataLooksMissing: hasAnySignal(routingContext, MISSING_DATA_PATTERNS),
    dataLooksComplete: hasAnySignal(routingContext, COMPLETE_DATA_PATTERNS),
    applicabilityInDoubt: false,
    knownExceptionPattern: false,
    ambiguousFailPoint:
      family.primaryAction.type === 'detail' ||
      family.primaryAction.type === 'rerun' ||
      family.primaryAction.type === 'highlight' ||
      family.targetPage === 'validation',
    scopeEligible: false,
  };

  for (const signal of STRONG_SCOPE_PATTERNS) {
    if (!hasAnySignal(routingContext, signal.patterns)) {
      continue;
    }

    addSignal(assessment, 'scope', 3, signal.reason, signal.tag);
    pushUnique(assessment.conditionStudioReasons, signal.reason);

    if (signal.tag === 'scope:exception-pattern') {
      assessment.knownExceptionPattern = true;
    }

    if (signal.tag === 'scope:applicability') {
      assessment.applicabilityInDoubt = true;
    }
  }

  for (const signal of PLAUSIBLE_SCOPE_PATTERNS) {
    if (!hasAnySignal(routingContext, signal.patterns)) {
      continue;
    }

    addSignal(assessment, 'scope', 2, signal.reason, signal.tag);
    pushUnique(assessment.conditionStudioReasons, signal.reason);

    if (signal.tag === 'scope:data-valid') {
      assessment.dataLooksComplete = true;
    }
  }

  if (assessment.repeatedPattern) {
    addSignal(
      assessment,
      'scope',
      2,
      'The same pattern repeats across multiple affected items or types.',
      'scope:repeated-pattern'
    );
    pushUnique(
      assessment.conditionStudioReasons,
      'The same pattern repeats across multiple affected items or types.'
    );
  }

  if (family.primaryAction.type === 'rerun') {
    addSignal(
      assessment,
      'detail',
      family.isBlocking ? 7 : 6,
      'This issue does not map cleanly to a known repair surface and needs exact inspection first.',
      'detail:manual-review'
    );

    if (family.isBlocking) {
      addSignal(
        assessment,
        'detail',
        1,
        'This blocker may require detail repair or an exact manual correction after inspection.',
        'detail:blocking-surgery'
      );
    }
  }

  if (family.primaryAction.type === 'detail') {
    addSignal(
      assessment,
      'detail',
      family.isBlocking ? 7 : 6,
      'This issue should be reviewed case by case in the repair workspace before choosing an exact fix.',
      'detail:repair-workspace'
    );
  }

  if (family.primaryAction.type === 'highlight') {
    addSignal(
      assessment,
      'detail',
      family.impactedElementIds.length > 0 ? 7 : 6,
      family.impactedElementIds.length > 0
        ? 'The failing point is already tied to model elements, so in-model inspection is the clearest first move.'
        : 'This issue needs model-side inspection before any broader change.',
      'detail:highlight'
    );
  }

  if (family.primaryAction.type === 'navigate') {
    if (family.targetPage === 'quick-fill') {
      addSignal(
        assessment,
        'repair',
        5,
        isObjectTypeCategoryMatchFamily(family)
          ? 'OBJECT-TYPE category compatibility has a known Quick Fill repair surface.'
          : 'Missing export data has a known Quick Fill repair surface.',
        'repair:quick-fill'
      );
    } else if (family.targetPage === 'mapping') {
      addSignal(
        assessment,
        'repair',
        5,
        'Mapping validation points to a direct mapping repair surface.',
        'repair:mapping'
      );
    } else if (family.targetPage === 'connectors') {
      addSignal(
        assessment,
        'repair',
        4,
        'Connector-bound fields can be reviewed directly in Connector binding.',
        'repair:connectors'
      );
    } else if (family.targetPage === 'properties') {
      addSignal(
        assessment,
        'repair',
        4,
        'The affected workflow fields can be reviewed directly in the properties workspace.',
        'repair:properties'
      );
    }
  }

  if (assessment.dataLooksMissing) {
    addSignal(
      assessment,
      'repair',
      2,
      'The current data path still looks incomplete, missing, or empty.',
      'repair:missing-data'
    );
  }

  if (family.isRequiredOnly) {
    addSignal(
      assessment,
      'repair',
      1,
      'This is a required validation family, so direct repair is usually the first path.',
      'repair:required-only'
    );
  }

  if (family.affectedCount <= 3 && family.issueCount <= 4) {
    addSignal(
      assessment,
      'repair',
      1,
      'The issue is concentrated in a small subset of clearly invalid items.',
      'repair:small-subset'
    );
  }

  if (family.category === 'required-export') {
    if (assessment.repeatedPattern && (assessment.knownExceptionPattern || assessment.applicabilityInDoubt || assessment.dataLooksComplete)) {
      addSignal(
        assessment,
        'scope',
        2,
        'Repeated valid-looking export gaps can indicate a narrower rule or intentional exception class.',
        'scope:required-export-hybrid'
      );
    }
  } else if (family.category === 'mapping') {
    if (assessment.repeatedPattern && (assessment.applicabilityInDoubt || assessment.dataLooksComplete)) {
      addSignal(
        assessment,
        'scope',
        2,
        'Repeated mapping failures can mean the mapping rule itself is broader than intended.',
        'scope:mapping-hybrid'
      );
    }
  } else if (family.category === 'properties' || family.category === 'components') {
    if (!family.isBlocking) {
      addSignal(
        assessment,
        'scope',
        1,
        'This issue family often depends on workflow or exception logic rather than pure missing data.',
        family.category === 'properties' ? 'scope:properties-family' : 'scope:components-family'
      );
      pushUnique(
        assessment.conditionStudioReasons,
        'This issue family often depends on workflow or exception logic rather than pure missing data.'
      );
    }
  }

  if (family.category === 'connectors' && family.primaryAction.type === 'navigate') {
    addSignal(
      assessment,
      'repair',
      1,
      'There is a connector-specific repair surface if inspection confirms a field-level issue.',
      'repair:connector-surface'
    );
  }

  if (assessment.ambiguousFailPoint) {
    addSignal(
      assessment,
      'detail',
      1,
      'The exact failing point is not obvious from the queue alone.',
      'detail:ambiguous-fail-point'
    );
  }

  const scopeThreshold =
    family.category === 'properties' || family.category === 'components'
      ? 4
      : family.category === 'mapping'
        ? 5
        : family.category === 'required-export'
          ? 6
          : family.primaryAction.type === 'rerun'
            ? 6
            : 7;

  assessment.scopeEligible =
    assessment.scopeReviewScore >= scopeThreshold &&
    (family.category !== 'connectors' || assessment.applicabilityInDoubt || assessment.knownExceptionPattern);

  if (!assessment.scopeEligible) {
    assessment.conditionStudioReasons = assessment.conditionStudioReasons.slice(0, 2);
  }

  return assessment;
}

function buildConfidenceLabel(confidence: ValidationDecisionConfidence): string {
  switch (confidence) {
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    default:
      return 'Low';
  }
}

function resolveRoutingDensity(params: {
  pathType: ValidationNextPathType;
  confidence: ValidationDecisionConfidence;
  primaryKind: ValidationDecisionActionKind;
  displayLabel: string;
}): ValidationRoutingDensity {
  const { pathType, confidence, primaryKind, displayLabel } = params;

  if (pathType === 'hybrid-review') {
    return 'medium';
  }

  if (displayLabel === 'Manual review') {
    return 'low';
  }

  if (pathType === 'scope-review') {
    return confidence === 'high' ? 'medium' : 'low';
  }

  if (pathType === 'detail-inspection') {
    return confidence === 'high' && primaryKind === 'highlight' ? 'high' : 'low';
  }

  if (confidence === 'high') {
    return 'high';
  }

  return confidence === 'medium' ? 'medium' : 'low';
}

function resolveConfidence(
  primaryPath: ValidationNextPathType,
  topScore: number,
  secondScore: number
): ValidationDecisionConfidence {
  const delta = topScore - secondScore;

  if (primaryPath === 'hybrid-review') {
    return delta >= 2 ? 'medium' : 'low';
  }

  if (delta >= 3) {
    return 'high';
  }

  if (delta >= 1) {
    return 'medium';
  }

  return 'low';
}

function getPathDisplay(
  pathType: ValidationNextPathType,
  family: ValidationIssueFamily,
  primaryKind: ValidationDecisionActionKind
): { label: string; subtitle?: string } {
  if (pathType === 'hybrid-review') {
    if (primaryKind === 'condition') {
      return {
        label: 'Hybrid: scope check',
        subtitle: 'Scope issue possible, but direct repair is still plausible.',
      };
    }

    return {
      label: 'Hybrid: repair first',
      subtitle: 'Repair likely, but scope review may be valid.',
    };
  }

  if (pathType === 'detail-inspection' && family.primaryAction.type === 'rerun') {
    return {
      label: 'Manual review',
      subtitle: 'Inspect the failing point before choosing a repair path.',
    };
  }

  if (pathType === 'detail-inspection') {
    return { label: 'Detail inspection' };
  }

  if (pathType === 'scope-review') {
    return { label: 'Scope review' };
  }

  return { label: 'Direct repair' };
}

function getAlternatePathLabel(pathType: ValidationNextPathType | null): string | undefined {
  switch (pathType) {
    case 'direct-repair':
      return 'Direct repair';
    case 'detail-inspection':
      return 'Detail inspection';
    case 'scope-review':
      return 'Scope review';
    case 'hybrid-review':
      return 'Hybrid review';
    default:
      return undefined;
  }
}

function buildSwitchGuidance(
  family: ValidationIssueFamily,
  primaryPath: ValidationNextPathType,
  primaryKind: ValidationDecisionActionKind,
  scopeAllowed: boolean
): ValidationSwitchPathGuidance[] {
  const directSignals = [
    'Required data is clearly missing.',
    'The mapped or export source is empty.',
    'The issue affects a small subset of clearly invalid items.',
  ];

  const scopeSignals = [
    'The same issue repeats across items that appear intentionally exempt.',
    'The current data already looks valid.',
    'Categories, workflows, or standards suggest the rule should be narrower.',
    'Repeated manual fixes would only mask a broader scope problem.',
  ];

  const detailSignals = [
    'The exact failing point is still unclear.',
    'The source path is ambiguous.',
    'Detail repair or item-level inspection is required before changing rule scope.',
  ];

  if (primaryPath === 'scope-review' || (primaryPath === 'hybrid-review' && primaryKind === 'condition')) {
    return [
      {
        pathType: 'direct-repair',
        label: `Switch to ${family.targetLabel}`,
        signals: directSignals,
      },
      {
        pathType: 'detail-inspection',
        label: 'Switch to detail workspace',
        signals: detailSignals,
      },
    ];
  }

  if (primaryPath === 'detail-inspection') {
    const guidance: ValidationSwitchPathGuidance[] = [];

    if (family.primaryAction.type === 'navigate') {
      guidance.push({
        pathType: 'direct-repair',
        label: `Switch to ${family.targetLabel}`,
        signals: directSignals,
      });
    }

    if (scopeAllowed) {
      guidance.push({
        pathType: 'scope-review',
        label: 'Switch to Condition Studio',
        signals: scopeSignals,
      });
    }

    return guidance;
  }

  const guidance: ValidationSwitchPathGuidance[] = [];

  if (scopeAllowed) {
    guidance.push({
      pathType: 'scope-review',
      label: 'Switch to Condition Studio',
      signals: scopeSignals,
    });
  }

  guidance.push({
    pathType: 'detail-inspection',
    label: 'Switch to detail workspace',
    signals: detailSignals,
  });

  return guidance;
}

function buildCompactSwitchGuidance(
  pathType: ValidationNextPathType,
  primaryKind: ValidationDecisionActionKind
): string | undefined {
  if (pathType === 'direct-repair') {
    return 'Use the detail workspace only if the source path remains unclear after repair.';
  }

  if (pathType === 'detail-inspection' && primaryKind === 'highlight') {
    return 'Switch to a repair surface only after model inspection points to a concrete field-level fix.';
  }

  if (pathType === 'detail-inspection') {
    return 'Switch to a repair surface only after inspection confirms the exact failing point.';
  }

  if (pathType === 'scope-review') {
    return 'Switch to direct repair only if inspection confirms a simple missing-data problem.';
  }

  return undefined;
}

function buildCompactAlternateActions(params: {
  family: ValidationIssueFamily;
  primaryPath: ValidationNextPathType;
  primaryKind: ValidationDecisionActionKind;
  scopeAllowed: boolean;
  alternatePath: ValidationNextPathType | null;
}): ValidationDecisionAction[] {
  const { family, primaryPath, primaryKind, scopeAllowed, alternatePath } = params;
  const actions: ValidationDecisionAction[] = [];

  if (
    (alternatePath === 'scope-review' || (primaryPath === 'hybrid-review' && primaryKind === 'repair')) &&
    scopeAllowed
  ) {
    actions.push(
      buildAction(
        'condition',
        'Open Condition Studio',
        'Use scope review if the current data already looks valid or the same exception pattern keeps repeating.'
      )
    );
  }

  if (alternatePath === 'direct-repair' || (primaryPath === 'hybrid-review' && primaryKind === 'condition')) {
    actions.push(
      buildAction(
        'repair',
        family.primaryAction.label,
        `Go to ${family.targetLabel} when the failing data is clearly missing and the repair surface is concrete.`
      )
    );
  }

  if (primaryPath !== 'detail-inspection') {
    actions.push(
      buildAction(
        'detail',
        'Open detail workspace',
        'Inspect the exact failing point first if the source path is still ambiguous.'
      )
    );
  }

  if (family.impactedElementIds.length > 0 && primaryKind !== 'highlight') {
    actions.push(
      buildAction(
        'highlight',
        'Highlight in model',
        'Check the affected model items when you need a quick reality check before changing anything broader.'
      )
    );
  } else if (primaryKind !== 'rerun') {
    actions.push(
      buildAction(
        'rerun',
        'Re-run validation',
        'Use after a concrete change to confirm which path actually resolved the issue.'
      )
    );
  }

  return actions.slice(0, 2);
}

export function resolveValidationIssueDecision(
  family: ValidationIssueFamily
): ValidationIssueDecision {
  const isObjectTypeCategoryMatch = isObjectTypeCategoryMatchFamily(family);
  const assessment = assessRoutingSignals(family);
  const directScore = assessment.directRepairScore;
  const scopeScore = assessment.scopeEligible ? assessment.scopeReviewScore : 0;
  const detailScore = assessment.detailInspectionScore;
  const rankedScores = [
    { path: 'direct-repair' as const, score: directScore },
    { path: 'scope-review' as const, score: scopeScore },
    { path: 'detail-inspection' as const, score: detailScore },
  ].sort((left, right) => right.score - left.score);
  const topScore = rankedScores[0]?.score ?? 0;
  const secondScore = rankedScores[1]?.score ?? 0;
  const isManualReview = family.primaryAction.type === 'rerun';
  const isDetailWorkspacePreferred = family.primaryAction.type === 'detail';
  const isConnectorInspection = family.category === 'connectors' && family.primaryAction.type === 'highlight';
  const directVsScopeClose = assessment.scopeEligible && Math.abs(directScore - scopeScore) <= 2;

  let pathType: ValidationNextPathType = 'direct-repair';
  let primaryKind: ValidationDecisionActionKind = 'repair';

  if (isManualReview) {
    pathType = 'detail-inspection';
    primaryKind = 'detail';
  } else if (isDetailWorkspacePreferred) {
    pathType = 'detail-inspection';
    primaryKind = 'detail';
  } else if (isConnectorInspection) {
    pathType = 'detail-inspection';
    primaryKind = family.impactedElementIds.length > 0 ? 'highlight' : 'detail';
  } else if (assessment.scopeEligible && scopeScore >= directScore + 3 && scopeScore >= detailScore + 2) {
    pathType = 'scope-review';
    primaryKind = 'condition';
  } else if (detailScore >= directScore + 3 && detailScore >= scopeScore + 2) {
    pathType = 'detail-inspection';
    primaryKind = family.impactedElementIds.length > 0 ? 'highlight' : 'detail';
  } else if (directVsScopeClose && directScore > 0) {
    pathType = 'hybrid-review';
    primaryKind = scopeScore > directScore ? 'condition' : 'repair';
  } else if (directScore >= detailScore) {
    pathType = 'direct-repair';
    primaryKind = 'repair';
  } else {
    pathType = 'detail-inspection';
    primaryKind = family.impactedElementIds.length > 0 ? 'highlight' : 'detail';
  }

  const confidence = resolveConfidence(pathType, topScore, secondScore);
  const display = getPathDisplay(pathType, family, primaryKind);
  const routingDensity = resolveRoutingDensity({
    pathType,
    confidence,
    primaryKind,
    displayLabel: display.label,
  });
  const alternatePath =
    pathType === 'hybrid-review'
      ? primaryKind === 'condition'
        ? 'direct-repair'
        : 'scope-review'
      : rankedScores.find((entry) => entry.path !== pathType && entry.score > 0)?.path ?? null;

  const confidenceReason =
    pathType === 'hybrid-review'
      ? primaryKind === 'repair'
        ? 'A direct repair surface exists, but the scope signals are strong enough that you should keep Condition Studio close.'
        : 'Scope signals lead slightly, but there is still a plausible direct repair surface.'
      : confidence === 'high'
        ? 'The current evidence points clearly to one dominant path.'
        : confidence === 'medium'
          ? 'One path leads, but a secondary route still has enough evidence to stay in play.'
          : 'The evidence is still split, so inspection should stay deliberate.';

  const reasonForAmbiguity =
    pathType === 'hybrid-review'
      ? primaryKind === 'repair'
        ? 'There is a clear repair surface, but repeated or valid-looking cases suggest the rule may be broader than intended.'
        : 'Scope signals are strong, but the issue still exposes a direct repair surface that could resolve it faster.'
      : undefined;

  const primaryAction =
    primaryKind === 'condition'
      ? buildAction(
          'condition',
          'Open Condition Studio',
          'Start with scope review when the rule itself may be too broad for these affected items.'
        )
      : primaryKind === 'detail'
        ? buildAction(
            'detail',
            family.isBlocking ? 'Start detail repair' : 'Open detail workspace',
            family.isBlocking
              ? 'Start in detail repair/manual review because the failing point needs exact correction before validation can clear.'
              : 'Inspect the failing point before choosing a broader repair or scope change.'
          )
        : primaryKind === 'highlight'
          ? buildAction(
              'highlight',
              'Highlight in model',
              'Start in-model because the connector or geometry failure is easiest to confirm visually.'
            )
          : buildAction(
              'repair',
              family.primaryAction.label,
              `Start in ${family.targetLabel} because this issue exposes a direct repair surface.`
            );

  const primaryActionSummary =
    isObjectTypeCategoryMatch && primaryKind === 'repair'
      ? 'Start with the category-aware OBJECT-TYPE repair path because the valid candidate values are constrained by each affected Revit category.'
      : primaryKind === 'condition'
      ? 'Start with Condition Studio because the issue looks more like a scope or applicability problem than a pure data gap.'
      : primaryKind === 'detail' || primaryKind === 'highlight'
        ? 'Inspect the exact failing point first, then decide whether the fix belongs in detail repair, model repair, or a narrower rule.'
        : `Start with ${family.targetLabel} because the repair surface is concrete and most likely to clear the issue fastest.`;

  const whyThisPath =
    pathType === 'scope-review'
      ? assessment.scopeSignals.slice(0, 3)
      : pathType === 'detail-inspection'
        ? assessment.detailSignals.slice(0, 3)
        : [
            ...(pathType === 'hybrid-review' ? assessment.repairSignals.slice(0, 2) : assessment.repairSignals.slice(0, 3)),
            ...(pathType === 'hybrid-review' && primaryKind === 'condition' ? assessment.scopeSignals.slice(0, 1) : []),
          ].slice(0, 3);

  const unresolvedReason =
    isObjectTypeCategoryMatch
      ? 'One or more OBJECT-TYPE values are incompatible with their Revit categories; repair should use only category-compatible OBJECT-TYPE candidates.'
      : pathType === 'scope-review'
      ? 'This issue family behaves more like a rule-applicability or exception-pattern problem than a pure direct fix.'
      : pathType === 'detail-inspection'
        ? 'The failure does not map cleanly to a safe direct repair surface yet.'
        : pathType === 'hybrid-review'
          ? 'This issue family has a repair surface, but rule scope may also explain the pattern.'
          : 'This is a fixable data, mapping, or connector-binding gap with a concrete repair surface.';

  const pathReason =
    isObjectTypeCategoryMatch
      ? 'Category-compatible OBJECT-TYPE values are known for this validation.'
      : pathType === 'scope-review'
      ? 'Repeated or exception-like patterns suggest the rule may be too broad.'
      : pathType === 'detail-inspection'
        ? 'The exact failing point needs inspection before repair.'
        : pathType === 'hybrid-review'
          ? 'Both direct repair and scope review have credible evidence.'
          : 'The issue can be fixed directly by correcting missing or incorrect export, mapping, or connector data.';

  const whyPath =
    isObjectTypeCategoryMatch
      ? `Use ${family.targetLabel} or auto-detect because the issue is a category compatibility mismatch, not a missing mapping problem.`
      : pathType === 'scope-review'
      ? 'Use Condition Studio because these items may be legitimate exceptions, category-specific cases, or evidence that the rule should be narrower.'
      : pathType === 'detail-inspection'
        ? 'Review the exact failing point before applying a repair.'
        : pathType === 'hybrid-review'
          ? primaryKind === 'condition'
            ? `Start with Condition Studio, but be ready to switch to ${family.targetLabel} if you confirm a simple missing-data problem.`
            : `Start with ${family.targetLabel}, but switch to Condition Studio if the current data already looks valid or the same pattern repeats across valid exceptions.`
          : 'Quick Fill is recommended because the missing or misaligned data can be corrected directly without broader review.';

  const switchGuidance = buildSwitchGuidance(
    family,
    pathType,
    primaryKind,
    assessment.scopeEligible
  );

  let alternateActions = buildCompactAlternateActions({
    family,
    primaryPath: pathType,
    primaryKind,
    scopeAllowed: assessment.scopeEligible,
    alternatePath,
  });

  if (routingDensity === 'high') {
    alternateActions = alternateActions
      .filter((action) => action.kind !== 'condition')
      .slice(0, 1);
  }

  return {
    pathType,
    pathLabel: display.label,
    pathSubtitle: display.subtitle,
    routingDensity,
    pathReason,
    unresolvedReason,
    whyPath,
    whyThisPath,
    prefersSurgery: primaryKind === 'detail' && family.primaryAction.type === 'rerun',
    confidence,
    confidenceLabel: buildConfidenceLabel(confidence),
    confidenceReason,
    primaryAction,
    primaryActionSummary,
    alternatePath,
    alternatePathLabel: getAlternatePathLabel(alternatePath),
    alternateActions,
    reasonForAmbiguity,
    repairSignals: assessment.repairSignals.slice(0, 3),
    scopeSignals: assessment.scopeSignals.slice(0, 3),
    detailSignals: assessment.detailSignals.slice(0, 3),
    switchGuidance,
    switchGuidanceDefaultOpen: routingDensity !== 'high',
    compactSwitchGuidance:
      routingDensity === 'high'
        ? isObjectTypeCategoryMatch
          ? 'Use the detail workspace only if the source category or current OBJECT-TYPE path remains unclear after the category-aware repair.'
          : buildCompactSwitchGuidance(pathType, primaryKind)
        : undefined,
    conditionStudioAllowed: assessment.scopeEligible,
    conditionStudioReason: assessment.scopeEligible
      ? assessment.conditionStudioReasons.slice(0, 2).join(' ')
      : 'Condition Studio stays hidden because the scope signals are not strong enough for this issue family.',
    telemetry: {
      primaryPath: pathType,
      alternatePath,
      confidence,
      ambiguityReason: reasonForAmbiguity,
      conditionStudioAllowed: assessment.scopeEligible,
      conditionStudioReasons: assessment.conditionStudioReasons.slice(0, 3),
      heuristicTags: assessment.heuristicTags,
      scoreBreakdown: {
        directRepair: directScore,
        scopeReview: scopeScore,
        detailInspection: detailScore,
      },
    },
  };
}
